// CloudWorkspaceSection — body-only variant av CloudWorkspaceDialog.
// Sparar och laddar krypterade workspaces från Supabase.
// Förberedande extract för #9 WorkspacePickerDialog-merge
// (POST_QA_SUBPLAN Wave E.2 step 3).
//
// Verbatim move: fetch-effect, save/load/delete-handlers,
// kryptering via encryptWorkspace/decryptWorkspace — identiska
// med originalet. State (docs/loading/saving/error/saveName)
// stannar internt så host-dialogen inte behöver tråda igenom.
//
// Användning:
//   - Standalone-dialog: CloudWorkspaceDialog (legacy wrapper).
//   - Tabs-läge: blivande WorkspacePickerDialog.

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { usePlan } from '@/hooks/usePlan';
import { supabase } from '@/integrations/supabase/client';
import { encryptWorkspace, decryptWorkspace } from '@/lib/workspaceEncryption';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import type { Json } from '@/integrations/supabase/types';
import {
  Save, FolderOpen, Trash2, Loader2, CloudOff, Lock,
  AlertCircle, Plus,
} from 'lucide-react';
import { UpgradeBadge } from '@/components/upgrade/UpgradeBadge';

interface CloudDoc {
  id: string;
  title: string;
  workflow_name: string;
  updated_at: string;
  is_pinned: boolean;
}

interface CloudWorkspaceSectionProps {
  /** Triggar fetch + reset av saveName när section:n blir öppen. */
  open: boolean;
  /** Stänger värd-dialogen efter lyckad load. */
  onClose: () => void;
  /** Returnerar aktuell workflow-state att kryptera + spara. */
  currentState: () => WorkflowState | MultiSheetWorkflowState;
  currentName: string;
  onLoad: (state: WorkflowState | MultiSheetWorkflowState, name: string, docId: string) => void;
  /** Rapporterar antal sparade docs upp till värd-dialogen så headerns
   *  "X/Y använda"-meta kan visas. */
  onDocsCountChange?: (count: number) => void;
}

export function CloudWorkspaceSection({
  open, onClose, currentState, currentName, onLoad, onDocsCountChange,
}: CloudWorkspaceSectionProps) {
  const { user } = useAuth();
  const { limits, canSaveToCloud } = usePlan();
  const [docs, setDocs] = useState<CloudDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveName, setSaveName] = useState('');

  const email = user?.email ?? '';
  const userId = user?.id ?? '';

  // Fetch user's cloud workspaces
  const fetchDocs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('workflows')
        .select('id, title, workflow_name, updated_at, is_pinned')
        .eq('created_by', userId)
        .order('updated_at', { ascending: false });

      if (err) throw err;
      setDocs((data as CloudDoc[]) ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch workspaces');
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    if (open && userId) {
      fetchDocs();
      setSaveName(currentName || '');
    }
  }, [open, userId, fetchDocs, currentName]);

  // Rapportera docs-count upp till värd-dialogen så headerns
  // "X/Y använda"-meta kan visas verbatim mot originalets UX.
  useEffect(() => {
    onDocsCountChange?.(docs.length);
  }, [docs.length, onDocsCountChange]);

  // Save current workspace to cloud (encrypted)
  const handleSave = async () => {
    if (!email || !userId) return;
    if (!saveName.trim()) { setError('Ange ett namn'); return; }
    if (!canSaveToCloud(docs.length)) {
      setError(`Du har nått gränsen (${limits.maxCloudStorage} arbetsytor). Uppgradera för fler.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const state = currentState();
      const encrypted = await encryptWorkspace(state, email);

      const { error: err } = await supabase.from('workflows').insert([{
        workflow_name: saveName.trim(),
        title: saveName.trim(),
        created_by: userId,
        blocks: encrypted as unknown as Json,
        connectors: [] as unknown as Json,
        groups: [] as unknown as Json,
        coordinate_origin: 'encrypted',
        category: 'user',
      }]);

      if (err) throw err;
      await fetchDocs();
      setSaveName('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
    setSaving(false);
  };

  // Load a workspace from cloud (decrypt)
  const handleLoad = async (doc: CloudDoc) => {
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('workflows')
        .select('*')
        .eq('id', doc.id)
        .single();

      if (err) throw err;

      let state: WorkflowState | MultiSheetWorkflowState;
      if (data.coordinate_origin === 'encrypted') {
        // Encrypted: blocks field holds the encrypted string. Payload may
        // be a single-sheet WorkflowState OR a MultiSheetWorkflowState —
        // decryptWorkspace returns the original object verbatim, and
        // downstream loadState() handles both shapes via isMultiSheetState.
        state = await decryptWorkspace<WorkflowState | MultiSheetWorkflowState>(
          data.blocks as unknown as string,
          email,
        );
      } else {
        // Legacy unencrypted (rare path — old documents pre-encryption)
        state = {
          name: data.workflow_name,
          blocks: data.blocks as unknown as WorkflowState['blocks'],
          connectors: data.connectors as unknown as WorkflowState['connectors'],
          groups: data.groups as unknown as WorkflowState['groups'],
          coordinateOrigin: 'center',
        };
      }

      onLoad(state, doc.title, doc.id);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunde inte dekryptera. Fel e-post?');
    }
    setLoading(false);
  };

  // Delete a workspace
  const handleDelete = async (docId: string) => {
    setError(null);
    try {
      const { error: err } = await supabase
        .from('workflows')
        .delete()
        .eq('id', docId)
        .eq('created_by', userId);

      if (err) throw err;
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-xs">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Save current workspace */}
      {limits.maxCloudStorage > 0 && (
        <div className="rounded-lg border p-4 space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Save className="w-4 h-4" />
            Spara till molnet
          </h3>
          <div className="flex gap-2">
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="Namn på arbetsyta..."
              className="flex-1"
            />
            <Button onClick={handleSave} disabled={saving || !saveName.trim()} size="sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Spara
            </Button>
          </div>
        </div>
      )}

      {limits.maxCloudStorage === 0 && (
        <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
          <CloudOff className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Molnlagring är inte tillgänglig på Free-planen.
          </p>
          <div className="flex justify-center">
            <UpgradeBadge tier="advanced" />
          </div>
        </div>
      )}

      {/* Saved workspaces list */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <FolderOpen className="w-4 h-4" />
          Sparade arbetsytor
        </h3>

        {loading && docs.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && docs.length === 0 && (
          <p className="text-xs text-muted-foreground py-4 text-center">
            Inga sparade arbetsytor ännu.
          </p>
        )}

        {docs.map(doc => (
          <div
            key={doc.id}
            className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent/30 transition-colors group"
          >
            <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{doc.title}</p>
              <p className="text-[10px] text-muted-foreground">
                {new Date(doc.updated_at).toLocaleDateString('sv-SE')} {new Date(doc.updated_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleLoad(doc)}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <FolderOpen className="w-4 h-4 mr-1" />
              Öppna
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(doc.id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

